import { SUBSCRIPTION_STATUS, subscriptionRules } from "@/libraries/constant";
import { usePrompt } from "@/stores/prompt";
import type { ButtonProps } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import upperFirst from "lodash/upperFirst";
import { useCallback } from "react";

interface UsePromptCollectionProps {
	currentPlan?: string;
}

function usePromptCollection(props: UsePromptCollectionProps = {}) {
	const { currentPlan = "free" } = props;
	const navigate = useNavigate();
	const openPrompt = usePrompt((state) => state.open);

	const upgradeCase = useCallback(
		(maxCase: number = subscriptionRules.maxCase[SUBSCRIPTION_STATUS.Free]) =>
			openPrompt({
				title: "Upgrade Required",
				description: `Dear colleague, thank you for using HEMI Teams. If you need to manage more than ${maxCase} cases per month, please upgrade now.`,
				positiveButton: {
					label: "Upgrade Now",
					onClick: () =>
						navigate({
							to: "/subscription",
						}),
				},
				negativeButton: {
					label: `Stay on ${upperFirst(currentPlan)} Plan`,
				},
			}),
		[openPrompt, navigate, currentPlan],
	);

	const freeCase = useCallback(
		(caseLeft: number, onPressNegative: ButtonProps["onClick"]) =>
			openPrompt({
				title: `You have ${caseLeft || 0} cases left`,
				description:
					"Upgrade your plan to increase your case quota and enjoy additional features.",
				positiveButton: {
					label: "Upgrade Now",
					onClick: () =>
						navigate({
							to: "/subscription",
						}),
				},
				negativeButton: {
					label: `Stay on ${upperFirst(currentPlan)} Plan`,
					onClick: onPressNegative,
				},
			}),
		[openPrompt, navigate, currentPlan],
	);

	const upgradeProPublishBulletin = useCallback(
		() =>
			openPrompt({
				title: "Pro Subscription Required",
				description:
					"Publishing bulletins is available only on the Pro plan. Upgrade to publish and share your medical updates.",
				positiveButton: {
					label: "Upgrade Now",
					onClick: () =>
						navigate({
							to: "/subscription",
						}),
				},
				negativeButton: {
					label: `Stay on ${upperFirst(currentPlan)} Plan`,
				},
			}),
		[openPrompt, navigate, currentPlan],
	);

	const upgradeBulletin = useCallback(
		(
			maxBulletin: number = subscriptionRules.subsBulletin[
				SUBSCRIPTION_STATUS.Free
			],
		) =>
			openPrompt({
				title: "Upgrade Required",
				description: `Dear colleague, thank you for using HEMI Teams. To follow more than ${maxBulletin} bulletin channels, please upgrade your plan.`,
				positiveButton: {
					label: "Upgrade Now",
					onClick: () =>
						navigate({
							to: "/subscription",
						}),
				},
				negativeButton: {
					label: `Stay on ${upperFirst(currentPlan)} Plan`,
				},
			}),
		[openPrompt, navigate, currentPlan],
	);

	const upgradeAllProBulletin = useCallback(
		() =>
			openPrompt({
				title: "All Members Need Pro Plan",
				description:
					"To publish a bulletin, all group members must be on the Pro plan. Please ask your team to upgrade their subscription.",
				positiveButton: {
					label: "OK",
				},
			}),
		[openPrompt],
	);

	const failedUploadFiles = useCallback(
		() =>
			openPrompt({
				title: "Error!",
				description: "Error sending image message to the chat",
				negativeButton: {
					label: "Ok",
					variant: "contained",
					color: "primary",
				},
			}),
		[openPrompt],
	);

	const acceptRequestAccessPrompt = useCallback(
		(onAccept: ButtonProps["onClick"]) =>
			openPrompt({
				title: "Accept Request?",
				description:
					"By accepting, the clinician will be able to view the common record and open their own case discussion.",
				positiveButton: {
					label: "Yes, Accept Request",
					onClick: onAccept,
				},
			}),
		[openPrompt],
	);

	const declineRequestAccessPrompt = useCallback(
		(onDecline: ButtonProps["onClick"]) =>
			openPrompt({
				title: "Decline Request?",
				description: "Are you sure you want to decline this access request?",
				positiveButton: {
					label: "Yes, Decline",
					color: "error",
					onClick: onDecline,
				},
			}),
		[openPrompt],
	);

	const hemiAiDisclaimer = useCallback(
		(onClick: ButtonProps["onClick"]) =>
			openPrompt({
				title: "Disclaimer",
				description:
					"HEMI Al suggestions are assistive only and not a substitute for clinical judgment or local guidelines",
				negativeButton: {
					label: "Ok",
					variant: "contained",
					color: "primary",
					onClick,
				},
			}),
		[openPrompt],
	);

	const deleteEvent = useCallback(
		(onClick: ButtonProps["onClick"]) =>
			openPrompt({
				title: "Delete Event?",
				description: "Are you sure you want to delete this event?",
				positiveButton: {
					label: "Yes, Delete",
					variant: "contained",
					color: "error",
					onClick,
				},
				negativeButton: {
					label: "Cancel",
					variant: "outlined",
					color: "primary",
				},
			}),
		[openPrompt],
	);

	const deleteTodo = useCallback(
		(onClick: ButtonProps["onClick"]) =>
			openPrompt({
				title: "Delete To-Do Item?",
				description: "Are you sure you want to delete this to-do item?",
				positiveButton: {
					label: "Yes, Delete",
					variant: "contained",
					color: "error",
					onClick,
				},
				negativeButton: {
					label: "Cancel",
					variant: "outlined",
					color: "primary",
				},
			}),
		[openPrompt],
	);

	return {
		upgradeCase,
		freeCase,
		upgradeBulletin,
		upgradeProPublishBulletin,
		upgradeAllProBulletin,
		failedUploadFiles,
		acceptRequestAccessPrompt,
		declineRequestAccessPrompt,
		hemiAiDisclaimer,
		deleteEvent,
		deleteTodo,
	};
}

export default usePromptCollection;
